<template>
  <Teleport to="#modals">
    <div class="drop-area" v-if="isVisibleDropArea">
      <div class="icon-container">
        <Icon name="image-plus" size="large" class="icon" />
      </div>
      <div class="text">
        <Text variant="h2">Upload photos here</Text>
        <div>Maximum 25MB photo</div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { Icon, Text } from '@tallo/design-system';

const emit = defineEmits<{ filesDrop: [fileList: FileList] }>();

const isVisibleDropArea = ref(false);
let dragTimer: ReturnType<typeof setTimeout> | undefined;

function onDragOver(event: DragEvent) {
  if (!isFilesValid(event)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  isVisibleDropArea.value = true;
  clearTimeout(dragTimer);
}

function onDragLeave(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();

  dragTimer = setTimeout(() => {
    isVisibleDropArea.value = false;
  }, 25);
}

function onDrop(event: DragEvent) {
  if (!isFilesValid(event)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  isVisibleDropArea.value = false;
  emit('filesDrop', event.dataTransfer!.files);
}

function isFilesValid(event: DragEvent) {
  return event?.dataTransfer?.types.includes('Files') && !!event.dataTransfer?.files;
}

onMounted(() => {
  window.addEventListener('dragover', onDragOver);
  window.addEventListener('dragleave', onDragLeave);
  window.addEventListener('drop', onDrop);
});

onUnmounted(() => {
  window.removeEventListener('dragover', onDragOver);
  window.addEventListener('dragleave', onDragLeave);
  window.addEventListener('drop', onDrop);
});
</script>

<style scoped lang="scss">
.drop-area {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--t-spacing-lg);
  color: var(--t-color-neutral-white);

  background-color: rgba(0, 0, 0, 0.5);

  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 5rem;
    height: 5rem;

    background-color: var(--t-color-neutral-100);
    border-radius: var(--t-border-radius-half-rounded);

    .icon {
      color: var(--t-color-neutral-950);
    }
  }

  .text {
    text-align: center;
  }
}
</style>
